<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" sm="9">
        <v-card style="margin-top:1em;" id="step3-patient-need">
          <v-card-title class="title d-print-none" style="color:white;background-color:#0a182a;">
            <span class="font-weight-bold" style="font-size:1.5rem; padding-right:5px;">{{ $t('step1_title') }}</span>
            {{ $t('How much oxygen does my ward/facility consume per day?') }}
          </v-card-title>
          <v-card-text class="d-print-none" style = "margin-top: 1em;">
            <v-btn-toggle
              active-class = "v-btn--active-teal"
              mandatory
              borderless
              style = "width: 100%;"
              v-model = "entermode3"
            >
              <v-btn class="multiline" tile large color="grey" dark style="width:50%; min-height:44px; height: auto;">
                {{ $t('step3_manual') }}
              </v-btn>
              <div style="width:50%; min-height:44px; height: auto; display:flex; flex-wrap:wrap;">
                <v-btn class="multiline" tile large color="grey" dark style="width:100%; min-height:44px; height: auto;" v-html="$t('demand_table_estimate')">
                </v-btn>
              </div>
            </v-btn-toggle>
          </v-card-text>
          <usage-table-editable show-date v-if="entermode3 === 0"/>
          <div
            v-else-if="entermode3 === 1"
            style = "margin: 0 8px;"
          >
            <inpatients />
            <proportion />
            <usage-table />
          </div>
        </v-card>
      </v-col>
      <v-col xs="12" class="d-sm-none">
        <summary-statistics />
      </v-col>
      <v-col sm="3" class="d-none d-sm-flex statistic-container" justify="center">
        <summary-statistics />
      </v-col>
      <v-overlay :value="showoverlay" v-on:click.native="showoverlay = false">
        <v-btn
          icon
          @click="showoverlay = false"
        >
          x
        </v-btn>
        <source-helper />
      </v-overlay>
      <v-overlay :value="showoverlay_cylinder" v-on:click.native="showoverlay_cylinder = false">
        <v-btn
          icon
          @click="showoverlay_cylinder = false"
        >
          x
        </v-btn>
        <cylinder-helper />
      </v-overlay>
      <v-dialog
        v-model="showdialog_save_database"
        persistent
        max-width="600px"
      >
        <v-card
          :loading="save_in_progress"
        >
          <template slot="progress">
            <v-progress-linear
              color="#178ccb"
              height="10"
              indeterminate
            ></v-progress-linear>
          </template>
          <v-card-title>
            <span class="text-h5">Save to Database</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-text-field
                    v-model="pin"
                    label="PIN"
                    hint="pin to allow saving to database"
                    persistent-hint
                    required
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-select
                    v-model="facility"
                    :items="facilities"
                    item-text="name"
                    item-value="id"
                    label="Facility"
                    required
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              @click="showdialog_save_database = false"
              :disabled="save_in_progress"
            >
              Cancel
            </v-btn>
            <v-btn
              color="blue darken-1"
              text
              @click="save_to_database"
              :disabled="!(pin && facility) || save_in_progress" 
            >
              Save
            </v-btn>
          </v-card-actions>
          <v-alert
            v-model="alert_show"
            :type="alert_type"
            dismissible
          >
            {{ alert_text }}
          </v-alert>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row class="d-print-none">
      <v-col cols = "12" sm="9">
        <v-card style="margin-top:1em;" id="step3-patient-need">
          <v-card-title class="title" style="color:white;background-color:#0a182a;">
            <span class="font-weight-bold" style="font-size:1.5rem; padding-right:5px;">{{ $t('step2_title') }}</span>
            {{ $t("How do I want to forecast?") }}
          </v-card-title>
          <v-card-text style = "margin-top: 1em;">
            <v-btn-toggle
              active-class = "v-btn--active-teal"
              mandatory
              borderless
              style = "width: 100%;"
              v-model = "demandmode"
            >
              <div style="width:50%; min-height:44px; height: auto; display:flex; flex-wrap:wrap;">
                <v-btn class="multiline" tile large color="grey" dark style="width:100%; min-height:44px; height: auto;">
                  {{ $t("Model for Me with Step 1 Data and Time Interval") }}
                </v-btn>
              </div>
              <v-tooltip v-if="entermode3 === 1" top max-width="60vw">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    style="width:50%; min-height:44px; height: auto; display:flex; flex-wrap:wrap; cursor: not-allowed;"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-btn
                      class="multiline"
                      tile
                      large
                      color="grey"
                      disabled
                      style="width:100%;"
                    >
                      {{ $t("Allow Me to Enter Daily Data") }}
                    </v-btn>
                  </div>
                </template>
                <span>{{ $t("Option only available") }}</span>
              </v-tooltip>
              <v-btn
                v-else
                class="multiline"
                tile
                large
                color="grey"
                dark
                style="width:50%; min-height:44px; height: auto;"
              >
                {{ $t("Allow Me to Enter Daily Data") }}
              </v-btn>
            </v-btn-toggle>
            <days-demand v-if="demandmode === 0"/>
            <div
              v-else-if="demandmode === 1"
              style = "margin: 0 8px;"
            >
              <usage-table-editable v-if="demandmode === 0"/>
              <v-row>
                <v-col cols="12">
                  <DemandHistoryTable />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <DemandHistoryChart />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="justify-center d-print-none">
      <v-btn tile x-large outlined @click="print">
        <icon-print />
        {{ $t('Print') }}
      </v-btn>
      <v-btn tile x-large outlined @click="download"
        v-if="entermode3 === 0"
      >
        <icon-save />
        {{ $t("Download") }}
      </v-btn>
      <v-btn 
        tile x-large outlined @click="initiate_save"
        v-if="ecuador && entermode3 === 0"
      >
        <icon-upload />
        {{ $t("Save to Cloud") }}
      </v-btn>
    </v-row>

  </v-container>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { csv } from 'd3-fetch';
// @ is an alias to /src

import UsageTable from '@/components/UsageTable.vue'
import UsageTableEditable from '@/components/UsageTableEditable.vue'
import Inpatients from '@/components/Inpatients.vue'
import Proportion from '@/components/Proportion.vue'
import DemandHistoryTable from '@/components/DemandHistoryTable.vue'
import DemandHistoryChart from '@/components/DemandHistoryChart.vue'

import SummaryStatistics from '@/components/SummaryStatisticsDemand.vue'
import SourceHelper from '@/components/SourceHelper4.vue'
import CylinderHelper from '@/components/CylinderHelper.vue'

import DaysDemand from '@/components/DaysDemand.vue'

import IconPrint from '@/components/IconPrint.vue'
import IconSave from '@/components/IconSave.vue'
import IconUpload from '@/components/IconUpload.vue'

const download_csv = function(arr) {
  var str = '';
  arr.forEach(rw => {
    let line = '';
    line = rw.join(",");
    str += line + '\r\n';
  });
  var blob = new Blob([str], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'oxygen_demand_history.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default {
  name: 'HomeDemand',

  props: {
    ecuador: Boolean,
  },

  components: {
    CylinderHelper,
    UsageTable,
    UsageTableEditable,
    DemandHistoryTable,
    DemandHistoryChart,
    Inpatients,
    Proportion,
    SummaryStatistics,
    SourceHelper,
    DaysDemand,
    IconPrint,
    IconSave,
    IconUpload,
  },

  data: () => ({
    supply_type: null,
    expanded_source: [],
    supply_type_plant: null,
    supply_type_liquid: null,
    supply_type_concentrator: null,
    showdialog_save_database: false,
    pin: null,
    facility: null,
    facilities: [
      {
        "id": "001145",
        "name": "HOSPITAL GENERAL ISIDRO AYORA"
      },
      {
        "id": "000101",
        "name": "HOSPITAL GENERAL VICENTE CORRAL MOSCOSO"
      },
      {
        "id": "001008",
        "name": "HOSPITAL GENERAL SAN VICENTE DE PAUL"
      },
      {
        "id": "001781",
        "name": "HOSPITAL DE ESPECIALIDADES EUGENIO ESPEJO"
      },
      {
        "id": "000844",
        "name": "ABEL GILBERT PONTON"
      },
      {
        "id": "002075",
        "name": "HOSPITAL GENERAL GUSTAVO DOMINGUEZ"
      },
      {
        "id": "002108",
        "name": "HOSPITAL GENERAL LIBORIO PANCHANA SOTOMAYOR"
      },
      {
        "id": "001783",
        "name": "HOSPITAL PEDIATRICO BACA ORTIZ"
      },
      {
        "id": "000707",
        "name": "HOSPITAL GENERAL ESMERALDAS SUR – “DELFINA TORRES DE CONCHA"
      },
      {
        "id": "001232",
        "name": "HOSPITAL GENERAL MARTIN ICAZA"
      },
      {
        "id": "001999",
        "name": "HOSPITAL GENERAL MARCO VINICIO IZA"
      },
      {
        "id": "001548",
        "name": "HOSPITAL GENERAL JOSE MARIA VELASCO IBARRA"
      },
      {
        "id": "000001",
        "name": "HOSPITAL GENERAL BABAHOYO (IEES)"
      }
    ],
    save_in_progress: false,
    alert_type: 'success',
    alert_text: '',
    alert_show: false,
  }),

  computed: {
    ...mapFields([
      'selected_plant_demand',
      'selected_concentrator_demand',
      'selected_liquid_demand',
      'selected_cylinder_demand',
      'entermode3',
      'entermode3b',
      'entermode3b_options',
      'showoverlay',
      'showoverlay_cylinder',
      'sources',
      'source',
      'usage',
      'distribution',
      'period',
      'leakage',
      'supply_types',
      'cylinder_types',
      'cylinder_type',

      'amount_plant_demand',
      'unit_plant_demand',
      'period_plant_demand',
      'days_demand',

      'demandmode',

      'patient_table_history',

      'trigger_savedialog',
      'demand_dirty',
    ]),
  },

  watch: {
    source: function() {
      if(this.source === 'Portable Oxygen Concentrator (PSA)') {
        // change to per minute default for concentrator
        this.period = 'per minute'
        // set leakage to 0 with concentrator since we will not include this input
        this.leakage = 0
      }
    },
    supply_type_plant: function() {
      if(this.supply_type_plant !== null) {
        this.amount_plant_demand = this.supply_type_plant.cbh
        this.unit_plant_demand = "cubic meters"
        this.period_plant_demand = "per hour"
      }
    },
    entermode3: function() {
      if(this.entermode3 === 1) {
        this.demandmode = 0;
      }
      if(this.entermode3 === 0) {
        this.demandmode = 1;
      }
    },
  },

  methods: {
    get_supply_types: function(source) {
      let types = this.supply_types ? this.supply_types.filter((d) => d.Manufacturer !== '') : []
      if(types.length === 0) {
        return types
      }
      if(source === "plant") {
        return types.filter( d => d["Oxygen supply type"] === "PSA Plant" || d["Oxygen supply type"] === "VSA Plant" || d["Oxygen supply type"] === "")
      }
      if(source === "concentrator") {
        return types.filter( d => d["Oxygen supply type"] === "Portable PSA" || d["Oxygen supply type"] === "")
      }
      if(source === "liquid") {
        return types.filter( d => d["Oxygen supply type"] === "Liquid Oxygen" || d["Oxygen supply type"] === "")
      }
    },
    print: function() {window.print()},
    download: function() {
      let export_dat = [["date", "device", "rate", "leak", "bias", "fio2", "rr", "expiratory", "liters", "patients"].map(x => this.$t(x))]

      Object.entries(this.patient_table_history).forEach( d => {
        if(Array.isArray(d[1]) && d[1].length > 0) {
          d[1].forEach(rw => {
            let dt = d[0]
            let row = [dt]
            const fields = ["device", "rate", "leak", "bias", "fio2", "rr", "expiratory", "liters", "patients"]
            fields.forEach( (ky) => {
              if(ky === "device") {
                // translate device
                row.push(this.$t(rw[ky]))
              } else {
                row.push(rw[ky])
              }
            })
            export_dat.push(row)
          })
        }
      })
      download_csv(export_dat)
    },
    send_to_database: async function(data, pin) {
      this.save_in_progress = true
      const response = await fetch('/.netlify/functions/savedatabase', {
        method: 'POST',
        body: JSON.stringify(
          {
            data_to_insert: data,
            pin: pin,
          }
        )
      })
      return response
    },
    save_to_database: function() {
      let instance = this
      let export_dat = []
      Object.entries(this.patient_table_history).forEach( d => {
        if(Array.isArray(d[1]) && d[1].length > 0) {
          d[1].forEach(rw => {
            let row = {date: d[0], loc_id: this.facility, ...rw}
            // some have text in row which we will not store in database
            delete row.text
            export_dat.push(row)
          })
        }
      })
      this.send_to_database(export_dat, this.pin)
        .then((response) => {
          console.log(response)
          if( response.status !== 200 ) {
            throw response.status
          }
          return response.json()
        })
        .then((data) => {
          instance.alert_type = 'success'
          instance.alert_text = 'Saved to database.'
          console.log('saved to database ', data)
        })
        .catch(function(error) {
          instance.alert_type = 'error'
          if(error === 401) {
            instance.alert_text = 'Incorrect pin provided.  Please try again.'
          } else {
            instance.alert_text = 'Error saving to database.'
          }
          console.log('error saving to database: ' + error)
        })
        .finally(function() {
          instance.save_in_progress = false
          instance.alert_show = true
          if(instance.alert_type === 'success') {
            setTimeout(
              function() {
                instance.alert_show = false
                instance.showdialog_save_database = false
              },
              2000
            )
          }
        })
    },
    initiate_save: function() {
      this.showdialog_save_database = true
      this.$nextTick(function() {
        this.trigger_savedialog = true
      })
    },
  },

  mounted: function() {
    let instance = this;
    csv('./supply_types.csv')
      .then( (result) => {
        instance.supply_types = result
      })
      .finally(function(e) {e})
  },

  beforeRouteEnter: (to, from, next) => {
    let name = to.name.toLowerCase()
    const isEcuador = name === "ecuador"
    name = name === "ecuador" ? "demand" : name
    next(vm => {
      vm.$store.commit('changeCalculator', name)
      if (to.query.lang) {
        vm.$store.commit('changeLocale', to.query.lang)
      } else if (isEcuador) {
        vm.$store.commit('changeLocale', 'es')
      }
    })
  }
}
</script>

<style scoped>
  .statistic-container {
    max-height: 85vh;
    position: -webkit-sticky;
    position: sticky;
    top: 4rem;
  }
</style>